import { z } from 'zod';

// ============================================
// DRAG ITEM TYPES
// ============================================

export const DragItemTypeSchema = z.enum([
  'drag-option',    // Draggable answer options
  'placed-chip',    // Chip already placed on a number-line axis (re-drag)
  'ruler-handle',   // Future: ruler endpoints
  'shape',          // Future: geometric shapes
  'number-card',    // Future: number cards for sorting
]);

export type DragItemType = z.infer<typeof DragItemTypeSchema>;

// ============================================
// DRAG OPTION DATA (for answer system)
// ============================================

export const DragOptionDataSchema = z.object({
  value: z.string(),
  label: z.string(),
});

export type DragOptionData = z.infer<typeof DragOptionDataSchema>;

// ============================================
// PLACED-CHIP DRAG ITEM (flat shape — no data wrapper)
// ============================================

/** Flat data shape carried by a chip dragged out of an input. */
export type PlacedChipItem = {
  type: 'placed-chip';
  widgetId: string;
  label: string;
  sourceOptionId?: string;
};

// ============================================
// GENERIC DRAG ITEM
// ============================================

export const DragItemSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    id: z.union([z.string(), z.number()]), // UniqueIdentifier from dnd-kit
    type: DragItemTypeSchema,
    data: dataSchema,
  });

/** Discriminated union over all drag item shapes. */
export type DragItem<T = unknown> =
  | { id: string | number; type: Exclude<DragItemType, 'placed-chip'>; data: T }
  | PlacedChipItem;

// ============================================
// SPECIFIC DRAG ITEM TYPES
// ============================================

export const DragOptionItemSchema = DragItemSchema(DragOptionDataSchema);
export type DragOptionItem = z.infer<typeof DragOptionItemSchema>;

// ============================================
// DROP RESULT
// ============================================

export const DropResultSchema = z.object({
  targetId: z.string(),
  item: z.discriminatedUnion('type', [
    z.object({
      id: z.union([z.string(), z.number()]),
      type: z.enum(DragItemTypeSchema.options.filter(t => t !== 'placed-chip') as [string, ...string[]]),
      data: z.unknown(),
    }),
    z.object({
      type: z.literal('placed-chip'),
      widgetId: z.string(),
      label: z.string(),
      sourceOptionId: z.string().optional(),
    }),
  ]),
});

export type DropResult = { targetId: string; item: DragItem };

// ============================================
// TOOL TYPES (for geometric tools)
// ============================================

export const ToolTypeSchema = z.enum([
  'ruler',
  'protractor',
  'set-square',
  'custom',
]);

export type ToolType = z.infer<typeof ToolTypeSchema>;

// ============================================
// POSITION AND ROTATION
// ============================================

export const PositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export type Position = z.infer<typeof PositionSchema>;

// ============================================
// TOOL CONFIG
// ============================================

export const ToolConfigSchema = z.object({
  id: z.string(),
  type: ToolTypeSchema,
  /** Only required for the 'custom' tool type. Geometry tools (ruler/protractor/set-square) render inline SVG. */
  imageSrc: z.string().optional(),
  initialPosition: PositionSchema.optional(),
  initialRotation: z.number().optional(),
  width: z.number().optional(),
});

export type ToolConfig = z.infer<typeof ToolConfigSchema>;
